import { useCallback, useEffect, useRef, useState } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { getDefaultData } from '../../lib/journal-utils.js';
import type { StudioCreateRequest } from './useStudioCommands.js';

/**
 * Where the prep text of one beat stands while Studio creates and links it.
 * Absent = nothing in flight (the beat has its text, or none was asked for).
 */
export interface BeatPrepProgress {
  /**
   * pending — awaiting the server; failed — certainly not done, «Retry» is
   * safe; unknown — the answer was lost with the connection, the outcome is
   * asked for after the rejoin (no «Retry»: it could duplicate); unresolved —
   * the server could not say (no record): the entry may exist, the GM checks
   * and chooses it, or creates a new one knowingly.
   */
  readonly status: 'pending' | 'failed' | 'unknown' | 'unresolved';
  /** entry — creating the Lore entry; link — linking that entry to the beat. */
  readonly step: 'entry' | 'link';
  /** The created entry, known from the link step on: a retry links it, never creates a second one. */
  readonly entryId?: string;
  readonly error?: string;
}

export interface CreateBeatArgs {
  readonly order: number;
  /** Beat label for messages and the prep entry name («Beat 3»). */
  readonly label: string;
  /** The beat this request created (matched by its requestId). */
  readonly onCreated: (beatId: string) => void;
  /** «Retry» of a refused create: the host's create action, run anew. */
  readonly retry: () => void;
}

interface BeatPrepDeps {
  readonly create: (request: StudioCreateRequest) => void;
  readonly campaignId: string;
  readonly t: (key: keyof TranslationKeys) => string;
  /**
   * The beat as of the latest render; undefined once deleted — or, for a beat
   * the server has just confirmed, not rendered yet (see `unseen` below).
   */
  readonly beatById: (beatId: string) => { readonly journalEntryId?: string | null } | undefined;
  readonly entryExists: (entryId: string) => boolean;
}

/**
 * Manual start of a beat (GM Studio package 3): create the beat, then its prep
 * text — a GM-only Lore entry (data.content) linked through the beat's
 * journalEntryId. Three server operations, each confirmed on its own:
 * beat:create → journal:create → beat:update { journalEntryId }. Nothing is
 * reported done before its answer; a failed step keeps what already exists
 * (no automatic deletion) and «Retry» continues from that step with the known
 * ids. Outcomes are matched by requestId (useStudioCommands), so a late or
 * foreign echo never completes another request; after the Studio closes or the
 * campaign changes nothing of this is awaited or retried.
 *
 * The chain of a beat lives only while the GM's intent stands: choosing or
 * clearing the beat's source by hand, or deleting the beat, cancels it at once
 * (not when the server confirms) — a late answer then links nothing, and a
 * created entry simply stays in the journal. A lost answer (dropped socket)
 * is an unknown outcome, not a failure: it is recovered after the rejoin —
 * the server says what the create made (request:outcome), a link is re-sent.
 *
 * Fast answers (GM Studio 4, review R2): beat:created and journal:created may
 * both arrive before React renders the new beat. A beat this hook created is
 * `unseen` until a render shows it; its absence then means «not rendered
 * yet», not «deleted», and the link waits for that render and is sent once.
 * Deleting it, choosing its source by hand, closing Studio or another campaign
 * still cancel the waiting link (generation / unmount).
 */
export function useBeatPrep(deps: BeatPrepDeps): {
  readonly prep: ReadonlyMap<string, BeatPrepProgress>;
  readonly creatingBeat: boolean;
  readonly createBeat: (args: CreateBeatArgs) => void;
  readonly startPrep: (beatId: string, label: string) => void;
  readonly resumePrep: (beatId: string) => void;
  /** The GM chose/cleared the source or deleted the beat: its chain is void. */
  readonly cancel: (beatIds: readonly string[]) => void;
} {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const { t } = deps;

  const prepRef = useRef(new Map<string, BeatPrepProgress>());
  const [prep, setPrep] = useState<ReadonlyMap<string, BeatPrepProgress>>(() => new Map());
  const labelsRef = useRef(new Map<string, string>());
  // One chain per beat: its generation is captured by every step; a step of an
  // older generation (cancelled, or superseded by «Add» again) does nothing.
  const generationRef = useRef(new Map<string, number>());
  // A second click while a beat:create is in flight sends nothing: the ref
  // guards clicks within one tick, the state disables the buttons.
  const creatingRef = useRef(false);
  const [creatingBeat, setCreatingBeat] = useState(false);
  // Beats this hook created that no render has shown yet.
  const unseenRef = useRef(new Set<string>());
  // Links waiting for their unseen beat to be rendered: entry and generation.
  const waitingLinkRef = useRef(new Map<string, { readonly entryId: string; readonly gen: number }>());

  const setProgress = useCallback((beatId: string, value: BeatPrepProgress | undefined): void => {
    const next = new Map(prepRef.current);
    if (value === undefined) next.delete(beatId);
    else next.set(beatId, value);
    prepRef.current = next;
    setPrep(next);
  }, []);

  const newGeneration = (beatId: string): number => {
    const gen = (generationRef.current.get(beatId) ?? 0) + 1;
    generationRef.current.set(beatId, gen);
    return gen;
  };
  const isCurrent = (beatId: string, gen: number): boolean => generationRef.current.get(beatId) === gen;

  const failTitle = useCallback(
    (beatId: string, step: 'entry' | 'link'): string =>
      t('studio.beatPanel.prepFailedToast')
        .replace('{beat}', labelsRef.current.get(beatId) ?? '')
        .replace('{step}', t(step === 'entry' ? 'studio.beatPanel.prepFailedEntry' : 'studio.beatPanel.prepFailedLink')),
    [t],
  );

  const resumeRef = useRef<(beatId: string) => void>(() => {});

  /** The beat still wants this text: it exists and nobody gave it a source meanwhile. */
  const beatStillWaits = (beatId: string): boolean => {
    const { beatById, entryExists } = depsRef.current;
    const beat = beatById(beatId);
    return beat !== undefined && !(beat.journalEntryId != null && entryExists(beat.journalEntryId));
  };

  const link = useCallback(
    (beatId: string, entryId: string, gen: number): void => {
      if (!isCurrent(beatId, gen)) return;
      if (unseenRef.current.has(beatId) && depsRef.current.beatById(beatId) === undefined) {
        // Confirmed, not rendered yet: link as soon as a render shows it.
        waitingLinkRef.current.set(beatId, { entryId, gen });
        setProgress(beatId, { status: 'pending', step: 'link', entryId });
        return;
      }
      if (!beatStillWaits(beatId)) {
        setProgress(beatId, undefined);
        return;
      }
      setProgress(beatId, { status: 'pending', step: 'link', entryId });
      depsRef.current.create({
        command: 'beat:update',
        echo: 'beat:updated',
        name: labelsRef.current.get(beatId) ?? '',
        failTitle: failTitle(beatId, 'link'),
        payload: { campaignId: depsRef.current.campaignId, beatId, changes: { journalEntryId: entryId } },
        // beat:updated carries no requestId: this beat, this entry — the entry id is fresh.
        matches: (echo) =>
          echo.beatId === beatId &&
          (echo.changes as { journalEntryId?: unknown } | undefined)?.journalEntryId === entryId
            ? entryId
            : undefined,
        onCreated: (): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, undefined);
        },
        onFailed: (reason: string): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, { status: 'failed', step: 'link', entryId, error: reason });
        },
        onUnknown: (reason: string): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, { status: 'unknown', step: 'link', entryId, error: reason });
        },
        // Setting the same source again changes nothing: safe to re-send after the rejoin.
        recover: 'resend',
        alive: (): boolean => isCurrent(beatId, gen),
        retry: (): void => resumeRef.current(beatId),
      });
    },
    // isCurrent / beatStillWaits read refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setProgress, failTitle],
  );

  const startPrep = useCallback(
    (beatId: string, label: string): void => {
      const current = prepRef.current.get(beatId);
      if (current !== undefined && (current.status === 'pending' || current.status === 'unknown')) return;
      const gen = newGeneration(beatId);
      labelsRef.current.set(beatId, label);
      setProgress(beatId, { status: 'pending', step: 'entry' });
      const name = depsRef.current.t('studio.beat.prepEntryName').replace('{beat}', label);
      depsRef.current.create({
        command: 'journal:create',
        echo: 'journal:created',
        name,
        failTitle: failTitle(beatId, 'entry'),
        // visibility omitted → the server default 'gm': players never get it.
        // P03: the prep text belongs to its beat (Q-2) — out of the Library lists.
        payload: { entryType: 'lore', name, data: getDefaultData('lore'), owner: { kind: 'beat', beatId } },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.entry as { id?: string } | undefined)?.id : undefined,
        onCreated: (entryId: string): void => link(beatId, entryId, gen),
        onFailed: (reason: string): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, { status: 'failed', step: 'entry', error: reason });
        },
        onUnknown: (reason: string): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, { status: 'unknown', step: 'entry', error: reason });
        },
        onUnresolved: (reason: string): void => {
          if (isCurrent(beatId, gen)) setProgress(beatId, { status: 'unresolved', step: 'entry', error: reason });
        },
        // After a lost answer the server is asked what this request created — never a second create.
        recover: 'ask',
        alive: (): boolean => isCurrent(beatId, gen),
        retry: (): void => resumeRef.current(beatId),
      });
    },
    // newGeneration / isCurrent read refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setProgress, failTitle, link],
  );

  // Retry (inline or from the toast — either may come first, the other is then
  // a no-op): continue a step that CERTAINLY failed; if meanwhile the beat was
  // deleted or got its text some other way, there is nothing left to do.
  const resumePrep = useCallback(
    (beatId: string): void => {
      const progress = prepRef.current.get(beatId);
      if (progress === undefined || progress.status !== 'failed') return;
      if (!beatStillWaits(beatId)) {
        setProgress(beatId, undefined);
        return;
      }
      if (progress.step === 'link' && progress.entryId !== undefined && depsRef.current.entryExists(progress.entryId)) {
        link(beatId, progress.entryId, newGeneration(beatId));
        return;
      }
      startPrep(beatId, labelsRef.current.get(beatId) ?? '');
    },
    // beatStillWaits / newGeneration read refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setProgress, link, startPrep],
  );
  resumeRef.current = resumePrep;

  const createBeat = useCallback(
    (args: CreateBeatArgs): void => {
      if (creatingRef.current) return;
      creatingRef.current = true;
      setCreatingBeat(true);
      const settle = (): void => {
        creatingRef.current = false;
        setCreatingBeat(false);
      };
      depsRef.current.create({
        command: 'beat:create',
        echo: 'beat:created',
        name: args.label,
        payload: { campaignId: depsRef.current.campaignId, order: args.order, status: 'draft' },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.beat as { id?: string } | undefined)?.id : undefined,
        onCreated: (beatId: string): void => {
          settle();
          unseenRef.current.add(beatId);
          args.onCreated(beatId);
          startPrep(beatId, args.label);
        },
        onFailed: settle,
        // Could not be told: the message asks the GM to check the plan; the buttons are free again.
        onUnresolved: settle,
        // Unknown: the buttons stay waiting until the outcome is asked for after the rejoin.
        recover: 'ask',
        retry: args.retry,
      });
    },
    [startPrep],
  );

  const cancel = useCallback(
    (beatIds: readonly string[]): void => {
      for (const beatId of beatIds) {
        newGeneration(beatId);
        waitingLinkRef.current.delete(beatId);
        if (prepRef.current.has(beatId)) setProgress(beatId, undefined);
      }
    },
    // newGeneration reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setProgress],
  );

  // After every render: a created beat the render shows is seen from now on —
  // its absence later means it is gone; a link waiting for it goes out once
  // (link re-checks the generation and whether the GM gave it a source).
  useEffect(() => {
    for (const beatId of [...unseenRef.current]) {
      if (depsRef.current.beatById(beatId) === undefined) continue;
      unseenRef.current.delete(beatId);
      const waiting = waitingLinkRef.current.get(beatId);
      if (waiting === undefined) continue;
      waitingLinkRef.current.delete(beatId);
      link(beatId, waiting.entryId, waiting.gen);
    }
  });

  // Another campaign: its beats and requests are not ours.
  useEffect(() => {
    return (): void => {
      prepRef.current = new Map();
      setPrep(prepRef.current);
      labelsRef.current.clear();
      generationRef.current.clear();
      unseenRef.current.clear();
      waitingLinkRef.current.clear();
      creatingRef.current = false;
      setCreatingBeat(false);
    };
  }, [deps.campaignId]);

  return { prep, creatingBeat, createBeat, startPrep, resumePrep, cancel };
}
